import { supabase, getCurrentUser } from './supabase';
import type {
  TrainingPlan,
  TrainingPlanPayload,
  TrainingSession,
  TrainingSessionExerciseLog,
} from '../types';
import { parsePlanExercises, serializePlanPayload } from './trainingPlanNormalize';

function mapPlanRow(row: Record<string, unknown>): TrainingPlan {
  return {
    id: row.id as string,
    user_id: row.user_id as string,
    name: row.name as string,
    exercises: parsePlanExercises(row.exercises),
    created_at: row.created_at as string,
    updated_at: row.updated_at as string,
  };
}

export async function fetchTrainingPlans(): Promise<TrainingPlan[]> {
  const { data, error } = await supabase
    .from('builder_workouts')
    .select('*')
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => mapPlanRow(row as Record<string, unknown>));
}

export async function createTrainingPlan(
  name: string,
  payload: TrainingPlanPayload
): Promise<TrainingPlan> {
  const user = await getCurrentUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('builder_workouts')
    .insert({ user_id: user.id, name, exercises: serializePlanPayload(payload) })
    .select()
    .single();
  if (error) throw error;
  return mapPlanRow(data as Record<string, unknown>);
}

export async function updateTrainingPlan(
  id: string,
  name: string,
  payload: TrainingPlanPayload
): Promise<TrainingPlan> {
  const { data, error } = await supabase
    .from('builder_workouts')
    .update({ name, exercises: serializePlanPayload(payload) })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return mapPlanRow(data as Record<string, unknown>);
}

export async function deleteTrainingPlan(id: string): Promise<void> {
  const { error } = await supabase.from('builder_workouts').delete().eq('id', id);
  if (error) throw error;
}

export async function saveTrainingSession(
  planId: string,
  dayId: string,
  startedAtIso: string,
  endedAtIso: string,
  log: TrainingSessionExerciseLog[]
): Promise<TrainingSession> {
  const user = await getCurrentUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('training_sessions')
    .insert({
      user_id: user.id,
      plan_id: planId,
      day_id: dayId,
      started_at: startedAtIso,
      ended_at: endedAtIso,
      log,
    })
    .select()
    .single();
  if (error) throw error;
  return data as TrainingSession;
}

export async function fetchAllTrainingSessions(): Promise<TrainingSession[]> {
  const { data, error } = await supabase
    .from('training_sessions')
    .select('*')
    .order('started_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as TrainingSession[];
}
